import { EventBus } from 'relapp-event';
import { CustomerModel } from 'relapp-customer-switch';

const state = {
    user: null,
    chat: {
        signature: null
    },
    currentCustomer: null,
    availableActions: null,
    originalUser: null
};

const setCustomers = user => {
    if (!user) {
        return null;
    }

    if (!user.customers) {
        if (user.customer) {
            // this happens if vuex and ajax action are out of sync.
            user.customers = [new CustomerModel(user.customer)];
        }

        return user;
    }

    const customers = user.customers.map(customer => {
        return new CustomerModel(customer);
    });

    return {
        ...user,
        customers
    };
};

const getters = {
    user: state => setCustomers(state.user),
    defaultCustomer: state => {
        const user = state.user;

        if (!user || !user.customers) {
            // this happens if vuex and ajax action are out of sync.
            if (user && !user.customers && user.customer) {
                return new CustomerModel(user.customer);
            }

            return null;
        }

        const customer = user.customers.find(customer => customer.id == state.user.customer_id);

        if (customer) {
            return new CustomerModel(customer);
        }
    },
    currentCustomer: state => {
        const user = state.user;

        if (!user || !user.customers) {
            // this happens if vuex and ajax action are out of sync.
            if (user && !user.customers && user.customer) {
                return new CustomerModel(user.customer);
            }

            return null;
        }

        if (!state.currentCustomer) {
            state.currentCustomer = user.customer_id;
        }

        const customer = user.customers.find(customer => customer.id == state.currentCustomer);

        if (customer) {
            return new CustomerModel(customer);
        }
    },
    originalUser: state => state.originalUser,
    can: state => ({ model, action }) => {
        // this happens if vuex and ajax action are out of sync.
        if (!state.availableActions && state.user && state.user.availableActions) {
            state.availableActions = state.user.availableActions;
        }
        if (state.availableActions && state.availableActions[model] && state.availableActions[model].includes(action)) {
            return true;
        }

        return false;
    },
    isLoggedIn: state => {
        if (state.user) {
            return true;
        }

        return false;
    }
};

const actions = {
    /**
     * Set the currently logged in user.
     *
     * @param {object} commit
     * @param {object} user
     */
    setUser({ commit }, user) {
        commit('setUser', user);
    },
    /**
     * Set the current customer id.
     *
     * @param {object} commit, state
     * @param {number} customerId
     */
    setCurrentCustomer({ commit, state }, customerId) {
        if (
            state.user &&
            state.user.customers &&
            customerId != state.currentCustomer &&
            state.user.customers.find(customer => customer.id == customerId)
        ) {
            const customer = state.user.customers.find(customer => customer.id == customerId);

            commit('setCurrentCustomer', customerId);
            EventBus.$emit('switchCustomer', customer);
        }
    },
    /**
     * Set the available actions for the currently logged in user.
     *
     * @param {object} commit
     * @param {object} availableActions
     */
    setAvailableActions({ commit }, availableActions) {
        commit('setAvailableActions', availableActions);
    },
    /**
     * Login.
     *
     * @param {object} commit
     * @param {object} user, originalUser
     */
    login({ commit }, { user, originalUser, availableActions, chat }) {
        commit('setUser', user);
        commit('setOriginalUser', originalUser);
        commit('setAvailableActions', availableActions);
        commit('setChat', chat);
        EventBus.$emit('userLoggedIn', { user, chatData: chat });
    },
    /**
     * Set the 2fa secret of the user.
     *
     * @param {object} commit
     * @param {string} secret
     */
    set2faSecret({ commit }, secret) {
        commit('set2faSecret', secret);
    },
    /**
     * Set the 2fa validated of the user.
     *
     * @param {object} commit
     * @param {boolean} validated
     */
    set2faValidated({ commit }, validated) {
        commit('set2faValidated', validated);
    },
    /**
     * Logout.
     * @param {object} commit, state
     */
    logout({ commit, state }) {
        let takenOver = false;

        if (state.originalUser) {
            takenOver = true;
        }

        if (state.user) {
            commit('reset');
            EventBus.$emit('userLoggedOut', {
                takenOver
            });
        }
    }
};

const mutations = {
    /**
     * Store the available actions.
     *
     * @param {object} state
     * @param {object} availableActions
     */
    setAvailableActions(state, availableActions) {
        state.availableActions = availableActions;
    },
    /**
     * Store the user.
     *
     * @param {object} state
     * @param {object} user
     */
    setUser(state, user) {
        state.user = user;

        if (!state.currentCustomer) {
            state.currentCustomer = user.customer_id;
        }
    },

    /**
     * Store the chat data.
     *
     * @param {object} state
     * @param {object} chat
     */
    setChat(state, chat) {
        state.chat = chat;
    },
    /**
     * Store the current customer id.
     *
     * @param {object} state
     * @param {number} customerId
     */
    setCurrentCustomer(state, customerId) {
        state.currentCustomer = customerId;
    },
    /**
     * Store the 2fa secret of the user.
     *
     * @param {object} state
     * @param {string} secret
     */
    set2faSecret(state, secret) {
        state.user.two_factor_authentication_secret = secret;
    },
    /**
     * Store the 2fa validated of the user.
     *
     * @param {object} state
     * @param {boolean} validated
     */
    set2faValidated(state, validated) {
        state.user.two_factor_authentication_validated = validated;
    },
    /**
     * Store the original user.
     *
     * @param {object} state
     * @param {object} originalUser
     */
    setOriginalUser(state, originalUser) {
        state.originalUser = originalUser;
    },
    /**
     * Reset the store.
     *
     * @param {object} state
     */
    reset(state) {
        state.availableActions = null;
        state.user = null;
        state.originalUser = null;
        state.currentCustomer = null;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
